import { getSidenavNotificationsButtonSx } from '@app/components/layout/sidenav/sidenav-colors';
import { getSidenavSidebarPopoverPosition } from '@app/components/layout/sidenav/sidenav-popover-utils';
import { AnchorPosition } from '@app/core/hooks';
import {
  SIDENAV_NOTIFICATIONS_TOUR_BELL_ID,
  useNotificationsTour,
} from '@app/modules/notifications/tour/useNotificationsTour';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { Badge, IconButton } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { SidenavNotificationsPopover } from './SidenavNotificationsPopover';

export type SidenavNotificationsButtonShellProps = {
  /** Unread count for the badge. */
  count?: number;
};

/**
 * Presentational notification bell + popover shell (stories and empty/ready trees).
 * Owns open state and runs the one-time notifications feature tour.
 */
export function SidenavNotificationsButtonShell({
  count = 0,
}: SidenavNotificationsButtonShellProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | undefined>();
  const hasNotifications = count > 0;
  const Icon = hasNotifications ? NotificationsOutlinedIcon : NotificationsNoneOutlinedIcon;

  /** Opens/closes the panel; when opening, anchors the popover to the bell. */
  const setOpenWithAnchor = useCallback((next: boolean) => {
    if (next && buttonRef.current) {
      setAnchorPosition(getSidenavSidebarPopoverPosition(buttonRef.current) ?? undefined);
    }
    setOpen(next);
  }, []);

  useNotificationsTour({ setOpen: setOpenWithAnchor });

  const handleToggle = () => {
    setOpenWithAnchor(!open);
  };

  return (
    <>
      <IconButton
        id={SIDENAV_NOTIFICATIONS_TOUR_BELL_ID}
        ref={buttonRef}
        size="small"
        onClick={handleToggle}
        sx={(theme) => getSidenavNotificationsButtonSx(theme, { hasNotifications })}
      >
        <Badge
          color="error"
          badgeContent={count}
          max={99}
          invisible={!hasNotifications}
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              typography: 'caption',
              // Compact chip for the 18px bell; default Badge is oversized here.
              height: 14,
              minWidth: 14,
              px: '3px',
              fontSize: '0.625rem',
              lineHeight: 1,
            },
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
      <SidenavNotificationsPopover
        open={open}
        anchorPosition={anchorPosition}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
